import { useState } from 'react';
import { SVLoader, SVTable } from '../core';
import { SVTableRow } from '@/types/common';
import { getTransactionReviewDeliverySpeed } from '@/utils/transactionReview';
import { ROUTES } from '@/constants/routes';
import TransactionReviewAmount from '../transactionReview/TransactionReviewAmount';
import { useGetTransactionRequestsList } from '@/hooks/requests/useTransactionRequests';
import { TRANSACTION_REQUEST_STATUS } from '@/types/transactionRequests';

type TransactionRequestListProps = {
  status: TRANSACTION_REQUEST_STATUS;
  bankAccountId: string;
};

export default function TransactionRequestList({
  status,
  bankAccountId,
}: TransactionRequestListProps) {
  const [page, setPage] = useState(0);
  const {
    transactionRequests,
    isTransactionRequestsListPending,
    totalRecordCount,
  } = useGetTransactionRequestsList({
    bankAccountUUID: bankAccountId,
    page,
    status,
  });

  if (isTransactionRequestsListPending) {
    return <SVLoader />;
  }

  const rows: SVTableRow[] = transactionRequests?.map(transaction => {
    const {
      uuid,
      requestedAt,
      displayAmount,
      description,
      deliverySpeed,
      owner,
      requestedBy,
    } = transaction;
    return {
      key: uuid,
      cells: [
        {
          key: `date${uuid}`,
          content: requestedAt,
          type: 'date',
        },
        {
          key: `amount${uuid}`,
          content: <TransactionReviewAmount amount={displayAmount} />,
          type: 'custom',
        },
        {
          key: `description${uuid}`,
          content: description,
          type: 'text',
        },
        {
          key: `speed${uuid}`,
          content: getTransactionReviewDeliverySpeed(deliverySpeed),
          type: 'text',
        },
        {
          key: `owner${uuid}`,
          content: `${owner.userName}`,
          type: 'text',
        },
        {
          key: `requester${uuid}`,
          type: 'text',
          content: `${requestedBy.userName}`,
        },
        {
          key: `details${uuid}`,
          type: 'link',
          content: `${ROUTES.BANK_ACCOUNTS}/${bankAccountId}${ROUTES.TRANSACTION_REQUESTS}/${uuid}`,
        },
      ],
    };
  });

  return (
    <SVTable
      ariaLabel={`${status} Transaction Requests`}
      headers={[
        { key: 'date', content: 'Date' },
        { key: 'amount', content: 'Amount' },
        { key: 'description', content: 'Description' },
        { key: 'speed', content: 'Speed' },
        { key: 'owner', content: 'Owner' },
        { key: 'requester', content: 'Requester' },
        { key: 'details', content: '' },
      ]}
      rows={rows}
      hasPagination
      page={page}
      setPage={setPage}
      totalRecordCount={totalRecordCount}
    />
  );
}
